import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  ChartGantt,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Plus,
} from "lucide-react";
import { useGetList, type Identifier } from "ra-core";
import type { Deal, OrganizationMember } from "@/components/atomic-crm/types";
import { getMemberName } from "@/components/atomic-crm/tasks/taskMemberOptions";
import { ModuleSearchField } from "@/components/atomic-crm/layout/ModuleToolbar";
import { ModuleFilterPopover } from "@/components/atomic-crm/layout/ModuleFilterPopover";
import { TaskEdit } from "@/components/atomic-crm/tasks/TaskEdit";
import { useTaskParticipantsByTaskIds } from "@/components/atomic-crm/tasks/useTaskParticipants";
import {
  useMarkTaskTagNotificationsRead,
  useUnreadTaskTagNotifications,
} from "@/components/atomic-crm/tasks/useTaskTagNotifications";
import type { TaskScopeFilter } from "@/components/atomic-crm/tasks/scopedTasks";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ViewTabs } from "@/modules/shared/chrome";
import { CalendarDayPanel } from "@/modules/calendar/CalendarDayPanel";
import { useCalendarEventReschedule } from "@/modules/calendar/useCalendarEventReschedule";
import { isMeetingRecord } from "@/modules/meetings/meetingFormat";
import { CalendarEventSheet } from "@/modules/calendar/CalendarEventSheet";
import {
  addDays,
  addMonths,
  formatMonthLabel,
  formatWeekLabel,
  groupEventsByDate,
  isCalendarEntryEvent,
  parseDateKey,
  toDateKey,
  type CalendarEntryEvent,
  type CalendarEvent,
  type CalendarView,
} from "@/modules/calendar/calendarUtils";
import { useCalendarBusinessHours } from "@/modules/calendar/useCalendarBusinessHours";
import { useCalendarAssigneeMembers } from "@/modules/calendar/useCalendarAssigneeMembers";
import { useIsAdminLevel } from "@/lib/permissions/useIsAdminLevel";
import { WorkCalendarListView } from "@/modules/work/WorkCalendarListView";
import { WorkCalendarView } from "@/modules/work/WorkCalendarView";
import { WorkTimelineView } from "@/modules/work/WorkTimelineView";
import { useTaskRangeReschedule } from "@/modules/work/useTaskRangeReschedule";
import {
  clampTimelineDayWidth,
  formatTimelineRangeLabel,
  getTimelineRange,
  getTimelineWeekCount,
  getTimelineZoomPreset,
  TIMELINE_ZOOM_PRESETS,
  type TimelineProjectSpan,
  type TimelineZoomPreset,
} from "@/modules/work/workTimeline";
import { getProjectDeliveryDate } from "@/modules/deals/projectDeliveryDate";
import { WorkCategoryChips } from "@/modules/work/WorkCategoryChips";
import { filterWorkItemsByCalendarPeriod } from "@/modules/work/workCategoryUtils";
import { cn } from "@/lib/utils";
import { filterWorkItemsBySearch } from "@/modules/work/workItemSearch";
import {
  useWorkPreferences,
  workPreferencesHaveActiveFilters,
  type WorkPreferences,
} from "@/modules/work/useWorkPreferences";
import { useWorkPageData } from "@/modules/work/useWorkPageData";
import type { WorkCreateCategory } from "@/modules/work/workCreateCategories";
import type { WorkItem } from "@/modules/work/workTypes";

const WORK_VIEW_TABS = [
  { value: "list", label: "List", icon: List },
  { value: "calendar", label: "Calendar", icon: LayoutGrid },
  { value: "timeline", label: "Timeline", icon: ChartGantt },
] as const;

const ALL_PROJECTS = "all";
const ALL_TEAM_MEMBERS = "all";
const getDealLabel = (deal: Deal) => deal.name?.trim() || `Project #${deal.id}`;
const TIMELINE_DAY_WIDTH_KEY = "nomi-crm:workTimelineDayWidth";
/** Off-screen weeks kept on each side of the timeline for endless scroll. */
const TIMELINE_BUFFER_WEEKS = 4;

const readTimelineDayWidth = () => {
  try {
    const stored = Number(window.localStorage.getItem(TIMELINE_DAY_WIDTH_KEY));
    return stored > 0
      ? clampTimelineDayWidth(stored)
      : TIMELINE_ZOOM_PRESETS.weeks;
  } catch {
    return TIMELINE_ZOOM_PRESETS.weeks;
  }
};

export const WorkPageContent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { preferences, setPreferences } = useWorkPreferences();
  const [anchor, setAnchor] = useState(() => new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [timelineDayWidth, setTimelineDayWidthState] =
    useState(readTimelineDayWidth);
  const setTimelineDayWidth = (width: number) => {
    const next = clampTimelineDayWidth(width);
    setTimelineDayWidthState(next);
    try {
      window.localStorage.setItem(TIMELINE_DAY_WIDTH_KEY, String(next));
    } catch {
      // Zoom is a per-browser convenience; ignore storage failures.
    }
  };
  const isTimeline = preferences.calendarDisplay === "timeline";
  // Infinite timeline: the visible weeks plus a scroll buffer on each side.
  // Reaching a buffer moves the anchor and the view keeps its place.
  const visibleTimelineWeeks = getTimelineWeekCount(timelineDayWidth);
  const timelineWeeks = visibleTimelineWeeks + 2 * TIMELINE_BUFFER_WEEKS;
  const timelineRange = useMemo(
    () => getTimelineRange(anchor, timelineWeeks, TIMELINE_BUFFER_WEEKS + 1),
    [anchor, timelineWeeks],
  );
  const visibleTimelineRange = useMemo(
    () => getTimelineRange(anchor, visibleTimelineWeeks),
    [anchor, visibleTimelineWeeks],
  );

  // Deep links (`?view=calendar|list|timeline`) pick the tab once.
  useEffect(() => {
    const view = searchParams.get("view");
    if (!view) return;
    if (view === "calendar" || view === "list" || view === "timeline") {
      setPreferences({ calendarDisplay: view });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next, { replace: true });
  }, [searchParams, setPreferences, setSearchParams]);

  const searchActive = Boolean(searchQuery.trim());

  const {
    memberId,
    projects,
    eventsByDate,
    workItems,
    searchableWorkItems,
    contactsById,
    categoryCounts,
    isPending,
  } = useWorkPageData({
    preferences,
    anchor,
    searchActive,
    timelineWeeks,
  });

  const { rescheduleEvent } = useCalendarEventReschedule();
  const { rescheduleTaskRange } = useTaskRangeReschedule();

  const filteredWorkItems = useMemo(() => {
    const base = searchActive
      ? filterWorkItemsBySearch(searchableWorkItems, searchQuery, contactsById)
      : workItems;
    return base;
  }, [contactsById, searchActive, searchQuery, searchableWorkItems, workItems]);

  const filteredEventsByDate = useMemo(
    () =>
      searchActive
        ? groupEventsByDate(filteredWorkItems.map((item) => item.event))
        : eventsByDate,
    [eventsByDate, filteredWorkItems, searchActive],
  );

  const isAdminLevel = useIsAdminLevel();
  const { membersById } = useCalendarAssigneeMembers(
    filteredEventsByDate,
    true,
  );
  const showAssigneeAvatars = true;

  const periodWorkItems = useMemo(
    () =>
      searchActive
        ? filteredWorkItems
        : filterWorkItemsByCalendarPeriod(
            filteredWorkItems,
            anchor,
            preferences.calendarView,
          ),
    [anchor, filteredWorkItems, preferences.calendarView, searchActive],
  );

  // Project summary bars follow the same filters as project deliveries:
  // shown with the Delivery type on, not narrowed to one teammate, and
  // matching the search when one is active.
  const timelineProjects = useMemo<TimelineProjectSpan[]>(() => {
    if (!isTimeline) return [];
    if (!preferences.categories.includes("delivery")) return [];
    if (preferences.teamMemberId != null) return [];
    const query = searchQuery.trim().toLowerCase();
    return projects
      .map((deal) => ({
        dealId: deal.id,
        name: deal.name?.trim() || `Project #${deal.id}`,
        startDate: deal.start_date ?? null,
        endDate: getProjectDeliveryDate(deal),
        companyId: deal.company_id ?? null,
        companyName: deal.company_name ?? null,
        description: deal.description ?? null,
        memberIds: deal.salesperson_ids?.length
          ? deal.salesperson_ids
          : deal.organization_member_id != null
            ? [deal.organization_member_id]
            : [],
      }))
      .filter(
        (project) => !query || project.name.toLowerCase().includes(query),
      );
  }, [
    isTimeline,
    preferences.categories,
    preferences.teamMemberId,
    projects,
    searchQuery,
  ]);

  const periodTaskIds = useMemo(
    () =>
      periodWorkItems.flatMap((item) =>
        item.event.kind === "task" ? [item.event.task.id] : [],
      ),
    [periodWorkItems],
  );

  const { participantsByTaskId } = useTaskParticipantsByTaskIds(periodTaskIds);

  const {
    notifications: unreadTagNotifications,
    total: unreadTaggedCount,
    refetch: refetchTagNotifications,
  } = useUnreadTaskTagNotifications(memberId);
  const { markRead } = useMarkTaskTagNotificationsRead();

  const [dayPanelOpen, setDayPanelOpen] = useState(false);
  const [dayPanelDateKey, setDayPanelDateKey] = useState(() =>
    toDateKey(new Date()),
  );
  const [calendarSelectedDateKey, setCalendarSelectedDateKey] = useState(() =>
    toDateKey(new Date()),
  );
  const [eventSheetOpen, setEventSheetOpen] = useState(false);
  const [eventSheetDateKey, setEventSheetDateKey] = useState(() =>
    toDateKey(new Date()),
  );
  const [eventSheetTime, setEventSheetTime] = useState<string | null>(null);
  const [eventSheetCategory, setEventSheetCategory] =
    useState<WorkCreateCategory>("task");
  const [editEventId, setEditEventId] = useState<Identifier | null>(null);
  const [editEventIsMeeting, setEditEventIsMeeting] = useState(false);
  const [editTaskId, setEditTaskId] = useState<string | number | null>(null);
  const [editTaskOpen, setEditTaskOpen] = useState(false);

  const { data: deals = [], isPending: isDealsPending } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
    },
    { staleTime: 60_000 },
  );

  const { data: teamMembers = [], isPending: isTeamMembersPending } =
    useGetList<OrganizationMember>(
      "organization_members",
      {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "first_name", order: "ASC" },
      },
      { enabled: isAdminLevel, staleTime: 60_000 },
    );

  const scopeOptions = useMemo(() => {
    const taggedLabel =
      unreadTaggedCount > 0 ? `Tagged me (${unreadTaggedCount})` : "Tagged me";
    return [
      { value: "mine" as const, label: "My tasks" },
      { value: "tagged" as const, label: taggedLabel },
      { value: "team" as const, label: "All team" },
      { value: "my_projects" as const, label: "My projects" },
    ];
  }, [unreadTaggedCount]);

  const fallbackDisplay = useMemo(
    () => ({
      showSaturday: preferences.showSaturday,
      showSunday: preferences.showSunday,
    }),
    [preferences.showSaturday, preferences.showSunday],
  );
  const { schedule: businessHoursSchedule } =
    useCalendarBusinessHours(fallbackDisplay);

  const displayOptions = businessHoursSchedule.displayOptions;

  const periodLabel = useMemo(() => {
    if (isTimeline) return formatTimelineRangeLabel(visibleTimelineRange);
    return preferences.calendarView === "month"
      ? formatMonthLabel(anchor)
      : formatWeekLabel(anchor);
  }, [anchor, isTimeline, preferences.calendarView, visibleTimelineRange]);

  const shiftPeriod = (direction: -1 | 1) => {
    setAnchor((current) => {
      if (isTimeline) {
        // Move about a third of the visible range.
        return addDays(
          current,
          direction * 7 * Math.max(2, Math.round(visibleTimelineWeeks / 3)),
        );
      }
      return preferences.calendarView === "month"
        ? addMonths(current, direction)
        : addDays(current, direction * 7);
    });
  };

  const openDayPanel = (dateKey: string) => {
    setCalendarSelectedDateKey(dateKey);
    setDayPanelDateKey(dateKey);
    setAnchor(parseDateKey(dateKey));
    setDayPanelOpen(true);
  };

  const closeDayPanel = () => setDayPanelOpen(false);

  const openEventSheet = ({
    dateKey,
    time = null,
    category = "task",
    eventId = null,
    isMeeting = false,
  }: {
    dateKey: string;
    time?: string | null;
    category?: WorkCreateCategory;
    eventId?: Identifier | null;
    isMeeting?: boolean;
  }) => {
    setEventSheetDateKey(dateKey);
    setEventSheetTime(time);
    setEventSheetCategory(category);
    setEditEventId(eventId);
    setEditEventIsMeeting(isMeeting);
    setDayPanelOpen(false);
    setEventSheetOpen(true);
  };

  const closeEventSheet = (nextOpen: boolean) => {
    setEventSheetOpen(nextOpen);
    if (!nextOpen) {
      setEditEventId(null);
      setEditEventIsMeeting(false);
      setEventSheetTime(null);
      setEventSheetCategory("task");
    }
  };

  const handleCalendarSelectDay = (dateKey: string) => {
    openDayPanel(dateKey);
  };

  const openEditReminder = (event: CalendarEntryEvent) => {
    openEventSheet({
      dateKey: event.date,
      eventId: event.record.id,
      isMeeting: isMeetingRecord(event.record),
    });
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    if (event.kind === "task") {
      setEditTaskId(event.task.id);
      setEditTaskOpen(true);
      setDayPanelOpen(false);
      return;
    }
    if (isCalendarEntryEvent(event)) {
      openEditReminder(event);
      return;
    }
    navigate(`/deals/${event.dealId}/show?tab=overview`);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    handleSelectEvent(event);
  };

  const handleSelectSlot = (dateKey: string, time: string) => {
    const daySchedule = businessHoursSchedule.getDaySchedule(dateKey);
    if (daySchedule.closed) return;
    if (
      businessHoursSchedule.enabled &&
      !businessHoursSchedule.isWithinBusinessHours(dateKey, time)
    ) {
      return;
    }
    openEventSheet({ dateKey, time, category: "scheduled_event" });
  };

  const handleSelectWorkItem = (item: WorkItem) => {
    handleSelectEvent(item.event);
  };

  const dayPanelEvents = filteredEventsByDate[dayPanelDateKey] ?? [];

  const markAllTaggedRead = async () => {
    await markRead(unreadTagNotifications);
    await refetchTagNotifications();
  };

  const hasAdvancedFilters =
    workPreferencesHaveActiveFilters(preferences) ||
    preferences.status !== "open" ||
    preferences.includeDoneTasks ||
    preferences.includeCompletedReminders ||
    (!businessHoursSchedule.enabled &&
      (!preferences.showSaturday || !preferences.showSunday));

  const filterSectionTitle =
    "text-2xs font-semibold tracking-wide text-muted-foreground uppercase";
  const filterFieldLabel = "text-xs text-muted-foreground";
  // Grouped: what to show, whose and which project, then how the calendar
  // looks.
  const workFilters = (
    <ModuleFilterPopover
      active={hasAdvancedFilters}
      ariaLabel="Filters"
      contentClassName="max-h-[min(36rem,80vh)] w-80 space-y-0 divide-y overflow-y-auto p-0"
    >
      <section className="space-y-2 p-3">
        <p className={filterSectionTitle}>Show</p>
        <WorkCategoryChips
          selected={preferences.categories}
          onChange={(categories) => setPreferences({ categories })}
          counts={categoryCounts}
        />
        <div className="space-y-1 pt-1">
          <Label className={filterFieldLabel}>Status</Label>
          <Select
            value={preferences.status}
            onValueChange={(value) =>
              setPreferences({
                status: value as WorkPreferences["status"],
              })
            }
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="space-y-2.5 p-3">
        <p className={filterSectionTitle}>People & project</p>
        <div className={cn("grid gap-2", isAdminLevel && "grid-cols-2")}>
          <div className="space-y-1">
            <Label className={filterFieldLabel}>Scope</Label>
            <Select
              value={preferences.scope}
              onValueChange={(value) => {
                const scope = value as TaskScopeFilter;
                const updates: Partial<WorkPreferences> = { scope };
                if (
                  scope === "mine" &&
                  preferences.teamMemberId != null &&
                  memberId != null &&
                  String(preferences.teamMemberId) !== String(memberId)
                ) {
                  updates.teamMemberId = null;
                }
                setPreferences(updates);
              }}
            >
              <SelectTrigger className="h-8 w-full text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scopeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isAdminLevel ? (
            <div className="space-y-1">
              <Label className={filterFieldLabel}>Team member</Label>
              <Select
                value={
                  preferences.teamMemberId != null
                    ? String(preferences.teamMemberId)
                    : ALL_TEAM_MEMBERS
                }
                onValueChange={(value) => {
                  const teamMemberId =
                    value === ALL_TEAM_MEMBERS ? null : value;
                  const updates: Partial<WorkPreferences> = { teamMemberId };
                  if (
                    teamMemberId != null &&
                    memberId != null &&
                    String(teamMemberId) !== String(memberId) &&
                    preferences.scope === "mine"
                  ) {
                    updates.scope = "team";
                  }
                  setPreferences(updates);
                }}
                disabled={isTeamMembersPending}
              >
                <SelectTrigger className="h-8 w-full text-xs">
                  <SelectValue placeholder="All members" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TEAM_MEMBERS}>All members</SelectItem>
                  {teamMembers.map((member) => (
                    <SelectItem key={member.id} value={String(member.id)}>
                      {getMemberName(member)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label className={filterFieldLabel}>Project</Label>
          <Select
            value={
              preferences.projectId != null
                ? String(preferences.projectId)
                : ALL_PROJECTS
            }
            onValueChange={(value) =>
              setPreferences({
                projectId: value === ALL_PROJECTS ? null : value,
              })
            }
            disabled={isDealsPending}
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_PROJECTS}>All projects</SelectItem>
              {deals.map((deal) => (
                <SelectItem key={deal.id} value={String(deal.id)}>
                  {getDealLabel(deal)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="space-y-2 p-3">
        <p className={filterSectionTitle}>Calendar display</p>
        {[
          ...(businessHoursSchedule.enabled
            ? []
            : [
                {
                  id: "work-show-saturday",
                  label: "Saturday",
                  checked: preferences.showSaturday,
                  onChange: (checked: boolean) =>
                    setPreferences({ showSaturday: checked }),
                },
                {
                  id: "work-show-sunday",
                  label: "Sunday",
                  checked: preferences.showSunday,
                  onChange: (checked: boolean) =>
                    setPreferences({ showSunday: checked }),
                },
              ]),
          {
            id: "work-show-completed-tasks",
            label: "Completed tasks",
            checked: preferences.includeDoneTasks,
            onChange: (checked: boolean) =>
              setPreferences({ includeDoneTasks: checked }),
          },
          {
            id: "work-show-done-events",
            label: "Done events",
            checked: preferences.includeCompletedReminders,
            onChange: (checked: boolean) =>
              setPreferences({ includeCompletedReminders: checked }),
          },
        ].map((option) => (
          <div
            key={option.id}
            className="flex items-center justify-between gap-3"
          >
            <Label htmlFor={option.id} className="text-sm font-normal">
              {option.label}
            </Label>
            <Switch
              id={option.id}
              checked={option.checked}
              onCheckedChange={option.onChange}
            />
          </div>
        ))}
        {businessHoursSchedule.enabled ? (
          <p className="text-xs text-muted-foreground">
            Weekends follow your business hours (Settings → Connectors →
            Twilio).
          </p>
        ) : null}
      </section>
    </ModuleFilterPopover>
  );

  if (isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[420px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 rounded-sm border bg-background px-1 py-0.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setAnchor(new Date())}
            >
              Today
            </Button>
            <IconButton
              onClick={() => shiftPeriod(-1)}
              aria-label="Previous period"
            >
              <ChevronLeft className="size-4" />
            </IconButton>
            <span className="min-w-[96px] text-center text-sm font-medium tabular-nums">
              {periodLabel}
            </span>
            <IconButton onClick={() => shiftPeriod(1)} aria-label="Next period">
              <ChevronRight className="size-4" />
            </IconButton>
          </div>

          <ViewTabs
            value={preferences.calendarDisplay}
            onChange={(value) => setPreferences({ calendarDisplay: value })}
            tabs={[...WORK_VIEW_TABS]}
            ariaLabel="View"
          />

          {!isTimeline ? (
            <ToggleGroup
              type="single"
              value={preferences.calendarView}
              onValueChange={(value) => {
                if (value === "month" || value === "week") {
                  setPreferences({ calendarView: value as CalendarView });
                }
              }}
              className="rounded-sm border bg-background p-0.5"
              aria-label="Period"
            >
              <ToggleGroupItem value="week" className="px-2.5 text-xs">
                Week
              </ToggleGroupItem>
              <ToggleGroupItem value="month" className="px-2.5 text-xs">
                Month
              </ToggleGroupItem>
            </ToggleGroup>
          ) : (
            <ToggleGroup
              type="single"
              value={getTimelineZoomPreset(timelineDayWidth)}
              onValueChange={(value) => {
                if (value in TIMELINE_ZOOM_PRESETS) {
                  setTimelineDayWidth(
                    TIMELINE_ZOOM_PRESETS[value as TimelineZoomPreset],
                  );
                }
              }}
              className="rounded-sm border bg-background p-0.5"
              aria-label="Timeline zoom"
            >
              {(["days", "weeks", "months"] as const).map((preset) => (
                <ToggleGroupItem
                  key={preset}
                  value={preset}
                  className="px-2.5 text-xs capitalize"
                  title="Tip: ⌘/Ctrl + scroll to zoom"
                >
                  {preset}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-x-4 gap-y-2">
          <ModuleSearchField
            value={searchQuery}
            onChange={setSearchQuery}
            basePlaceholder="Search tasks"
            total={filteredWorkItems.length}
            itemSingular="item"
            className="w-56 flex-none xl:w-72"
            trailing={workFilters}
          />
          {preferences.scope === "tagged" &&
          unreadTagNotifications.length > 0 ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={markAllTaggedRead}
            >
              <CheckCheck className="size-4" />
              Mark all read
            </Button>
          ) : null}

          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              openEventSheet({ dateKey: toDateKey(new Date()) });
            }}
          >
            <Plus className="size-4" />
            New task
          </Button>
        </div>
      </div>

      <div className="relative min-w-0 space-y-3">
        {preferences.calendarDisplay === "calendar" ? (
          <>
            <WorkCalendarView
              anchor={anchor}
              view={preferences.calendarView}
              eventsByDate={filteredEventsByDate}
              displayOptions={displayOptions}
              schedule={businessHoursSchedule}
              selectedDateKey={calendarSelectedDateKey}
              onSelectDay={handleCalendarSelectDay}
              onSelectEvent={handleSelectEvent}
              onEditEvent={handleEditEvent}
              onSelectSlot={handleSelectSlot}
              onRescheduleEvent={rescheduleEvent}
              showAssigneeAvatars={showAssigneeAvatars}
              membersById={membersById}
            />
            <CalendarDayPanel
              dateKey={dayPanelDateKey}
              events={dayPanelEvents}
              schedule={businessHoursSchedule}
              open={dayPanelOpen}
              onClose={closeDayPanel}
              onSelectEvent={handleSelectEvent}
              onEditEvent={handleEditEvent}
              onSelectSlot={handleSelectSlot}
              onAddEvent={(dateKey) => openEventSheet({ dateKey })}
              showAssigneeAvatars={showAssigneeAvatars}
              membersById={membersById}
            />
          </>
        ) : isTimeline ? (
          <WorkTimelineView
            items={filteredWorkItems}
            range={timelineRange}
            restScrollDays={TIMELINE_BUFFER_WEEKS * 7}
            onReachEdge={(direction) =>
              setAnchor((current) =>
                addDays(current, direction * TIMELINE_BUFFER_WEEKS * 7),
              )
            }
            dayWidth={timelineDayWidth}
            onDayWidthChange={setTimelineDayWidth}
            onSelectItem={handleSelectWorkItem}
            onRescheduleEvent={rescheduleEvent}
            onRescheduleTaskRange={rescheduleTaskRange}
            projects={timelineProjects}
            emptyMessage={
              searchActive
                ? "No matches for this search."
                : "Nothing scheduled in these weeks."
            }
          />
        ) : (
          <WorkCalendarListView
            items={periodWorkItems}
            participantsByTaskId={participantsByTaskId}
            onSelectItem={handleSelectWorkItem}
            emptyMessage={
              searchActive
                ? "No matches for this search."
                : "Nothing scheduled in this period."
            }
          />
        )}
      </div>

      <CalendarEventSheet
        open={eventSheetOpen}
        onOpenChange={closeEventSheet}
        dateKey={eventSheetDateKey}
        initialTime={eventSheetTime}
        initialCategory={eventSheetCategory}
        editEventId={editEventId}
        editIsMeeting={editEventIsMeeting}
      />

      {editTaskId != null ? (
        <TaskEdit
          taskId={editTaskId}
          open={editTaskOpen}
          onOpenChange={setEditTaskOpen}
        />
      ) : null}
    </div>
  );
};
